import * as layout from '../../../components/markLayout.module.css';
import Table from '@hero/ui-kit/tables/Table';
import type { TableColumn } from '@hero/ui-kit/tables/Table/TableColHeader';
import React from 'react';
import P from '@hero/ui-kit/typography/P';
import InlineLoader from '../../../../../components/InlineLoader';
import QueryInlineError from '../../../../../components/QueryInlineError';
import useBlastStats from '../../../api/useBlastStats';
import { SKIPPED_REASON_LABELS } from '../../../constants';
import type { BlastStatus, BlastByVariant, BlastTimeSeriesBucket } from '../../../types';
type Props = {
    blastId: number;
    status: BlastStatus;
};
const pct = (n: number | null): string => (n != null ? `${(n * 100).toFixed(1)}%` : '—');
const Tile: React.FC<{
    label: string;
    value: string | number;
    subValue?: string;
}> = ({ label, value, subValue }) => (
    <div
        style={{
            textAlign: 'center',
            minWidth: '10rem'
        }}
        className={layout.panel}
    >
        <P
            noDefaultMargin
            strong
            styles={{
                fontSize: '2rem'
            }}
        >
            {typeof value === 'number' ? value.toLocaleString() : value}
        </P>
        <P
            noDefaultMargin
            size="small"
            styles={{
                color: 'var(--mark-muted)'
            }}
        >
            {label}
        </P>
        {subValue && (
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-muted)',
                    marginTop: '0.2rem'
                }}
            >
                {subValue}
            </P>
        )}
    </div>
);
const variantColumns: TableColumn<BlastByVariant>[] = [
    {
        colKey: 'Variant',
        name: 'Variant',
        render: (v) => {
            return <>{v.label}</>;
        }
    },
    {
        colKey: 'Ratio',
        name: 'Ratio',
        align: 'right',
        headAlign: 'right',
        render: (v) => {
            return <>{v.ratio}%</>;
        }
    },
    {
        colKey: 'Sent',
        name: 'Sent',
        align: 'right',
        headAlign: 'right',
        render: (v) => {
            return <>{v.sent.toLocaleString()}</>;
        }
    },
    {
        colKey: 'Delivered',
        name: 'Delivered',
        align: 'right',
        headAlign: 'right',
        render: (v) => {
            return <>{v.delivered.toLocaleString()}</>;
        }
    },
    {
        colKey: 'Open',
        name: 'Open',
        align: 'right',
        headAlign: 'right',
        render: (v) => {
            return <>{pct(v.open_rate)}</>;
        }
    },
    {
        colKey: 'Click',
        name: 'Click',
        align: 'right',
        headAlign: 'right',
        render: (v) => {
            return <>{pct(v.click_rate)}</>;
        }
    },
    {
        colKey: 'Unsub',
        name: 'Unsub',
        align: 'right',
        headAlign: 'right',
        render: (v) => {
            return <>{pct(v.unsubscribe_rate)}</>;
        }
    },
    {
        colKey: 'Bounced',
        name: 'Bounced',
        align: 'right',
        headAlign: 'right',
        render: (v) => {
            return <>{v.bounced.toLocaleString()}</>;
        }
    }
];
const timeSeriesColumns = (
    maxSent: number,
    bucketLabel: string
): TableColumn<BlastTimeSeriesBucket>[] => [
    {
        colKey: 'Time',
        name: bucketLabel,
        render: (b) => {
            return (
                <>
                    {/* Day buckets are UTC days (BE TIME_ZONE=UTC); local rendering would shift the date. */}
                    {new Date(b.timestamp).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: b.bucket_type === 'h' ? '2-digit' : undefined,
                        minute: b.bucket_type === 'h' ? '2-digit' : undefined,
                        timeZone: b.bucket_type === 'h' ? undefined : 'UTC',
                        timeZoneName: 'short'
                    })}
                </>
            );
        }
    },
    {
        colKey: 'Sent',
        name: 'Sent',
        align: 'right',
        headAlign: 'right',
        render: (b) => {
            return <>{b.sent.toLocaleString()}</>;
        }
    },
    {
        colKey: 'Volume',
        name: 'Volume',
        render: (b) => {
            const barWidth = maxSent > 0 ? (b.sent / maxSent) * 100 : 0;
            return (
                <>
                    <div
                        style={{
                            height: '1.2rem',
                            width: `${barWidth}%`,
                            backgroundColor: 'var(--mark-primary)',
                            borderRadius: '0.2rem',
                            minWidth: barWidth > 0 ? '2px' : 0
                        }}
                    />
                </>
            );
        }
    },
    {
        colKey: 'Opened',
        name: 'Opened',
        align: 'right',
        headAlign: 'right',
        render: (b) => {
            return <>{b.opened.toLocaleString()}</>;
        }
    },
    {
        colKey: 'Clicked',
        name: 'Clicked',
        align: 'right',
        headAlign: 'right',
        render: (b) => {
            return <>{b.clicked.toLocaleString()}</>;
        }
    }
];
const Stats: React.FC<Props> = ({ blastId, status }) => {
    const { data: statsResponse, isLoading, error } = useBlastStats(blastId, status);
    const stats = statsResponse?.data;
    if (isLoading) return <InlineLoader />;
    if (error) return <QueryInlineError error={error} />;
    if (!stats) return <P>No stats available.</P>;
    const { totals, by_variant, time_series } = stats;
    const skipped = totals.skipped_by_reason;
    const hourly = time_series.filter((b) => b.bucket_type === 'h');
    const daily = time_series.filter((b) => b.bucket_type === 'd');
    const maxHourlySent = Math.max(1, ...hourly.map((b) => b.sent));
    const maxDailySent = Math.max(1, ...daily.map((b) => b.sent));
    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    gap: '0.8rem',
                    flexWrap: 'wrap',
                    marginBottom: '1.6rem'
                }}
            >
                <Tile label="Sent" value={totals.sent} />
                <Tile label="Delivered" value={totals.delivered} />
                <Tile label="Opened" value={totals.opened} subValue={pct(totals.open_rate)} />
                <Tile label="Clicked" value={totals.clicked} subValue={pct(totals.click_rate)} />
                <Tile label="Bounced" value={totals.bounced} />
                <Tile label="Complained" value={totals.complained} />
                <Tile
                    label="Unsubscribed"
                    value={totals.unsubscribed}
                    subValue={pct(totals.unsubscribe_rate)}
                />
                <Tile label="Skipped" value={totals.skipped} />
            </div>
            {totals.skipped > 0 && (
                <div
                    style={{
                        marginBottom: '1.6rem'
                    }}
                    className={layout.tableScroll}
                >
                    <P
                        strong
                        noDefaultMargin
                        styles={{
                            marginBottom: '0.6rem'
                        }}
                    >
                        Skipped by reason
                    </P>
                    <Table
                        columns={[
                            { colKey: 'reason', name: 'Reason' },
                            {
                                colKey: 'count',
                                name: 'Skipped',
                                align: 'right',
                                headAlign: 'right',
                                render: (row) => row.count.toLocaleString()
                            }
                        ]}
                        data={(Object.keys(skipped) as (keyof typeof skipped)[])
                            .filter((k) => skipped[k] > 0)
                            .map((k) => ({
                                reason: SKIPPED_REASON_LABELS[k] ?? k,
                                count: skipped[k]
                            }))}
                        rowKey="reason"
                    />
                </div>
            )}
            {by_variant.length > 1 && (
                <div
                    style={{
                        marginBottom: '1.6rem',
                        overflowX: 'auto'
                    }}
                    className={layout.tableScroll}
                >
                    <P
                        strong
                        noDefaultMargin
                        styles={{
                            marginBottom: '0.6rem'
                        }}
                    >
                        By variant
                    </P>
                    <Table columns={variantColumns} data={by_variant} rowKey="label" />
                </div>
            )}
            {hourly.length > 0 && (
                <div
                    style={{
                        marginBottom: '1.6rem',
                        overflowX: 'auto'
                    }}
                    className={layout.tableScroll}
                >
                    <P
                        strong
                        noDefaultMargin
                        styles={{
                            marginBottom: '0.6rem'
                        }}
                    >
                        Hourly (first 48h)
                    </P>
                    <Table
                        columns={timeSeriesColumns(maxHourlySent, 'Time')}
                        data={hourly}
                        rowKey="timestamp"
                    />
                </div>
            )}
            {daily.length > 0 && (
                <div
                    style={{
                        overflowX: 'auto'
                    }}
                    className={layout.tableScroll}
                >
                    <P
                        strong
                        noDefaultMargin
                        styles={{
                            marginBottom: '0.6rem'
                        }}
                    >
                        Daily (from day 3)
                    </P>
                    <Table
                        columns={timeSeriesColumns(maxDailySent, 'Date')}
                        data={daily}
                        rowKey="timestamp"
                    />
                </div>
            )}
        </div>
    );
};
export default Stats;
